import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import he from 'he';
import axiosPrivate from 'config/axiosPrivate';

import '../admin/textDisplay.css';

// Redux Store
import {
  adminClientRedactionDataSelector,
  clearAdminClientRedactionData,
  setCurrentRedaction,
} from 'store/AdminClient/RedactionData';
import {
  fetchAdminClientTextOrderDisplay,
  fetchadminCLientOrderDisplay,
} from 'store/AdminClient/OrderDisplay/features';
import { adminCLientOrderDisplaySelector } from 'store/AdminClient/OrderDisplay';
import { UserSessionSelector } from 'store/UserSession';

// Context Providers
import { WordsProvider } from 'utils/contexts/ScoreBoldWordsContext';
import { SemanticScoreProvider } from 'utils/contexts/SemanticScoreContext';

// Components
import { Form } from '@unform/web';
import AnalyseText from 'components/Admin/AnalyseText';
import { CustomEditor } from 'components/shared/CustomEditor';
import { Loader } from 'components/shared/Loader';
import ComparisonModal from 'components/shared/modal/ComparisonModal';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import SavingIndicator from 'components/shared/AutoSaveDisplay';
import KeywordStuffingModal from 'components/shared/modal/KeywordStuffingModals/KeywordStuffingModal';
import { TextDisplaySidebar } from 'components/shared/TextDisplaySidebar/TextDisplaySidebar';
import { InitialTextSection } from 'components/shared/InitialTextSection';
import { FormHeader } from 'components/shared/TextDisplay/FormHeader';
import { TextDetailsSection } from 'components/shared/TextDisplay/TextDetailsSection';
import { LanguageDropdown } from 'components/shared/TextDisplay/LanguageDropdown';
import { WordCountDisplay } from 'components/shared/TextDisplay/WordCountDisplay';
import { DownloadOneTextWordType } from 'components/shared/DownloadFile/DownloadTextWordType';
import { RevisionDetails } from 'components/shared/RevisionDetails';

// Helpers
import { checkDataAdmin } from 'helpers/formHelper';
import {
  extractStrKeywordsWithFrequency,
  transformToSemanticGap,
  extractKeywordsWithFrequency,
  formatKeywordData,
} from 'helpers/FormatKeywordsHelper';
import { autoSaveText } from 'helpers/CreateAutoSave';


import {
  ROLES,
  GOVERNED_WRITERS_WITH_VALIDATION,
  KeywordStuffingModalPrompts,
} from 'utils/Constants';
import { getScoringLanguageLabel } from 'helpers/ProjectLanguageHelper';
import { extractWords } from 'utils/keywordStuffing/keywordStuffing';
import { keywordsAutoGeneration } from 'utils/KeywordsAutoGeneration/keywordsAutoGeneration';

import { useAutosave } from 'utils/hooks/useAutoSave';
import { useSidebar } from 'utils/hooks/features/textDisplay/useSidebar';
import { useEditorManagement } from 'utils/hooks/features/textDisplay/useEditorManagement';
import { useWordCountTracker } from 'utils/hooks/features/textDisplay/useWordCountTracker';
import { useTextStatus } from 'utils/hooks/features/textDisplay/useTextStatus';

import { useAdminClientTextForm } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientTextForm';
import { useAdminClientTextSave } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientTextSave';
import { useAdminClientDeliverText } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientDeliverText';
import { useAdminClientKeywordStuffing } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientKeywordStuffing';
import { useAdminClientFormValidation } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientFormValidation';
import { useAdminClientPutToDraft } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientPutToDraft';
import { useAdminClientAutoSave } from 'utils/hooks/features/textDisplay/adminClient/useAdminClientAutoSave';
import usePageTracking from 'utils/hooks/usePageTracking';

import { replaceTextInBold, entitiesToSpecialChars } from 'utils/StringUtils';

const PROJECT_MANAGER_TEXT_DETAILS_PAGE = '/ProjectManager/TextDetails';
const wordsInBold = [];
const semanticScore = 0;

function PMTextDisplayContent() {
  const RED = '#EE3900';
  const ORANGE = '#FF8D4B';
  const BLACK = '#3c3c3c';

  const [analyseCount, setAnalyseCount] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isFetchLoading, setIsFetchLoading] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isSendRevisionDisabled, setIsSendRevisionDisabled] = useState(false);
  const [isDraftDisabled, setIsDraftDisabled] = useState(true);

  const [confirmation, setConfirmation] = useState(false);
  const [confirmationMsg, setConfirmationMsg] = useState('');

  const [showComparison, setShowComparison] = useState(false);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [errorSuccessImg, setErrorSuccessImg] = useState(undefined);
  const [isSuccess, setIsSuccess] = useState(false);

  const [color, setColor] = useState(BLACK);
  const [allWebsitesArePresent, setAllWebsitesArePresent] = useState(false);
  const [autoKeywordsGenerationLoading, setAutokeywordGenerationLoading] =
    useState(false);
  const [validTextNbr, setValidTextNbr] = useState(false);
  const [isAutomaticTextAnalysis, setIsAutomaticTextAnalysis] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);

  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const { user } = useSelector(UserSessionSelector);
  const { currentRedaction, isLoading, isError } = useSelector(
    adminClientRedactionDataSelector
  );
  const { order } = useSelector(adminCLientOrderDisplaySelector);




  const redactionData = currentRedaction;

  const projectLanguage = useMemo(
    () =>
      getScoringLanguageLabel(
        order?.website?.language || order?.language
      ),
    [order?.website?.language, order?.language]
  );

  useEffect(() => {
    const projectId = redactionData?.projectId || redactionData?.orderId;
    if (!projectId) return;
    if (String(order?.id) === String(projectId)) return;
    dispatch(fetchadminCLientOrderDisplay(projectId));
  }, [redactionData?.projectId, redactionData?.orderId, order?.id, dispatch]);

  const [pendingTextType, setPendingTextType] = useState(() => {
    try {
      return window.localStorage.getItem('WriterClient_PendingTextType') || '';
    } catch {
      return '';
    }
  });

  const normalizedPendingTextType = (pendingTextType || '').toUpperCase();

  const normalizedOrderTextType = (
    order?.textType ||
    normalizedPendingTextType ||
    ''
  ).toUpperCase();

  const normalizedRedactionTextType = (
    redactionData?.type ||
    redactionData?.textType ||
    normalizedPendingTextType ||
    redactionData?.frontendType ||
    ''
  ).toUpperCase();
  const isTranslationText =
    normalizedOrderTextType === 'TRANSLATION' ||
    normalizedRedactionTextType === 'TRANSLATION' ||
    normalizedRedactionTextType === 'TRANSLATED';
  const uiTextType = isTranslationText ? 'TRANSLATION' : 'EDITORIAL';
  const backendTextType =
    redactionData?.type ||
    (isTranslationText ? 'TRANSLATED' : 'NON_TRANSLATED');

  const [currentContentScore, setCurrentContentScore] = useState(null);

  const formRef = useRef(null);
  const isInitialized = useRef(false);

  const {
    sidebarWidth,
    setSidebarWidth,
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarRef,
    handleOpenSidebarAndScrollTop,
    showSidebar,
    handleScrollBottom,
  } = useSidebar(uiTextType);

  const shouldDisplaySidebar = showSidebar && !isTranslationText;

  useEffect(() => {
    if (normalizedRedactionTextType) {
      try {
        window.localStorage.removeItem('WriterClient_PendingTextType');
      } catch { }
      if (pendingTextType) {
        setPendingTextType('');
      }
    }
  }, [normalizedRedactionTextType, pendingTextType]);

  console.log('WriterClient sidebar debug', {
    normalizedOrderTextType,
    normalizedRedactionTextType,
    isTranslationText,
    showSidebar,
    shouldDisplaySidebar,
  });


  const {
    textTitle,
    keyword,
    url,
    titleTag,
    metaDescriptionTag,
    actualTextContent,
    actualHtmlContent,
    analyseText,
    analyseResult,
    keywords,
    combinedKeywords,
    list,
    stuffedKeywords,
    websites,
    initialTextContent,
    initialHtmlContent,
    // Setters
    setTextTitle,
    setKeyword,
    setUrl,
    setTitleTag,
    setMetaDescriptionTag,
    setActualTextContent,
    setActualHtmlContent,
    setAnalyseText,
    setAnalyseResult,
    setKeywords,
    setCombinedKeywords,
    setCombinedKeywordsList,
    setList,
    setStuffedKeywords,
    setWebsites,
    setInitialTextContent,
    setInitialHtmlContent,
  } = useAdminClientTextForm(redactionData, 'SMART', isLoading);

  const {
    editorRef,
    stepOneEditorRef,
    editorInitialized,
    stepOneEditorInitialized,
    handleEditorInit,
    handleStepOneEditorInit,
  } = useEditorManagement();

  const {
    wordCount: initialTextWordCount,
    setWordCount: setInitialTextWordCount,
    getWordCount,
  } = useWordCountTracker(stepOneEditorRef, stepOneEditorInitialized);

  const {
    wordCount: actualTextWordCount,
    debouncedSetWordCount: debouncedSetActualTextWordCount,
  } = useWordCountTracker(editorRef, editorInitialized);


  const {
    isPremium: hasInitialSection,
    hasInitialTextContent,
    isNotTranslation: isEditorial,
    isDelivered,
    isRevisionDone,
    isRevisionSent,
    isDraft,
    isEmpty,
  } = useTextStatus(redactionData, 'SMART', user.role, uiTextType);

  const { deliverText, isTextDelivered, setIsTextDelivered } =
    useAdminClientDeliverText({
      t,
      textType: uiTextType,
      combinedKeywords,
      setErrorSuccessMsg,
      setIsSuccess,
      setErrorSuccess,
      titleTag,
      metaDescriptionTag,
      setIsFetchLoading,
      actualHtmlContent,
      actualTextContent,
      semanticScore,
      initialHtmlContent,
      initialTextContent,
      keyword,
      textTitle,
      actualTextWordCount,
      initialTextWordCount,
      url,
      websites,
      setErrorSuccessImg,
      redactionId: redactionData?.id,
      formData: redactionData,
      currentContentScore: currentContentScore,
    });

  const {
    keywordStuffingLoading,
    initialStuffedKeywords,
    showKeywordStuffingModal,
    keywordStuffingModalTitle,
    keywordStuffingModalMsg,
    keywordStuffingModalData,
    keywordStuffingModalCloseBtn,
    keywordStuffingCall,
    setShowKeywordStuffingModal,
    setInitialStuffedKeywords,
  } = useAdminClientKeywordStuffing({
    setSidebarCollapsed,
    handleScrollBottom,
    actualHtmlContent,
    setActualHtmlContent,
    textTitle,
    keywords,
    extractWords,
    textType: uiTextType,
    initialTextContent,
    actualTextContent,
    initialTextWordCount,
    actualTextWordCount,
    setStuffedKeywords,
    deliverText,
    t,
    setActualTextContent,
    setInitialHtmlContent,
  });

  const { handleSave, isTextFieldDisabled } = useAdminClientTextSave({
    redactionData: redactionData,
    initialHtmlContent,
    initialTextContent,
    actualTextContent,
    actualHtmlContent,
    actualTextWordCount,
    initialTextWordCount,
    combinedKeywords,
    keyword,
    metaDescriptionTag,
    textTitle,
    titleTag,
    url,
    textType: backendTextType,
    stuffedKeywords,
    websites,
    initialStuffedKeywords,
    analyseResult,
    redactionId: redactionData?.id,
    setConfirmation,
    setActualTextContent,
    setActualHtmlContent,
    setErrorSuccessMsg,
    setErrorSuccessImg,
    setIsSuccess,
    setErrorSuccess,
    t,
    websiteId: redactionData?.websiteId || order?.websiteId || 0,
    aiAgentId: redactionData?.aiAgentId || '',
    aiAgentModel: redactionData?.aiAgentModel || '',
    projectId: redactionData?.projectId || redactionData?.orderId || order?.id || null,
    currentContentScore: currentContentScore,
    currentSemanticGap: list,
  });

  const { putToDraft } = useAdminClientPutToDraft({
    t,
    actualHtmlContent,
    actualTextContent,
    analyseResult,
    keyword,
    metaDescriptionTag,
    list,
    combinedKeywords,
    websites,
    stuffedKeywords,
    textTitle,
    titleTag,
    url,
    textId: redactionData?.id,
    projectId: order?.id || redactionData?.orderId,
    initialTextContent,
    initialHtmlContent,
    initialStuffedKeywords,
    possibleStuffedKeywords: [],
    actualTextWordCount,
    initialTextWordCount,
    type: backendTextType,
    activeInitialText: redactionData?.activeInitialText,
    totalNumberOfTextAdded: 0,
    contentScore: currentContentScore || redactionData?.contentScore || null,
    writerProjectId: redactionData?.writerProjectId || 0,
    websiteId: redactionData?.websiteId || order?.websiteId || 0,
    aiAgentId: redactionData?.aiAgentId || '',
    currentSemanticGap: list,
    setErrorSuccessMsg,
    setErrorSuccessImg,
    setIsSuccess,
    setErrorSuccess,
  });

  const { autoSave } = useAdminClientAutoSave({
    textId: redactionData?.id,
  });

  const { scheduleSave, isSaving } = useAutosave(autoSave, 30000);

  const { checkValidForm, disabledForm } = useAdminClientFormValidation({
    actualTextContent,
    actualTextWordCount,
    analyseResult,
    formData: redactionData,
    initialTextWordCount,
    keyword,
    metaDescriptionTag,
    minWords: 0,
    textTitle,
    textType: uiTextType,
    titleTag,
    validTextNbr,
    semanticScore,
  });

  const enableSave = useCallback(() => {
    if (!redactionData || isEmpty(initialTextContent) || isEmpty(url)) {
      setIsSaveDisabled(true);
      return;
    }
    setIsSaveDisabled(false);
  }, [redactionData, initialTextContent, url]);

  function handleKeywordChange(e) {
    const newValue = e.target.value;
    setCombinedKeywords(newValue);
    setKeywords(extractStrKeywordsWithFrequency(newValue));

    const newGap = transformToSemanticGap(newValue);
    setList(newGap);
  }

  function handleGapUpdate(newGap) {
    setList(newGap);
    setKeywords(extractKeywordsWithFrequency(newGap));
    setCombinedKeywords(formatKeywordData(newGap));
  }

  const enableSendRevision = useCallback(() => {
    if (redactionData && redactionData.revisionDone) {
      setIsSendRevisionDisabled(true);
      return;
    }
    setIsSendRevisionDisabled(false);
  }, [redactionData]);

  const enableDraft = useCallback(() => {

    const hasContent = actualTextContent && actualTextContent.trim().length > 0;
    setIsDraftDisabled(!hasContent);
  }, [actualTextContent]);

  async function validateData(data, { reset }, event) {
    let action = event.nativeEvent.submitter.value;
    console.log('🔍 Action du bouton:', action);

    let finalHtmlContent = actualHtmlContent;

    if (action === 'PutToDraft' || action === 'DeliverText') {

      finalHtmlContent = await applyHighlightingAndWait();
    }

    const wrappedPutToDraft = (demande) => {

      return putToDraft(demande, finalHtmlContent);
    };
    const wrappedDeliverText = (demande) =>
      deliverText(demande, finalHtmlContent);

    console.log('🔍 Appel à checkDataAdmin avec action:', action);
    await checkDataAdmin(
      data,
      'SMART', // orderType
      formRef,
      action,
      wrappedPutToDraft,
      wrappedDeliverText
    );
  }

  function resetFields() {
    setTextTitle('');
    setKeyword('');
    setUrl('');
    setTitleTag('');
    setMetaDescriptionTag('');
    setActualTextContent('');
    setActualHtmlContent('');
    setInitialTextContent('');
    setInitialHtmlContent('');
    setAnalyseResult('');
    setKeywords('');
    setCombinedKeywords('');
    setWebsites('');
    setInitialStuffedKeywords([]);
    setStuffedKeywords([]);
    setList({});
    setIsTextDelivered(false);
  }

  const handleInputChange = (e) => {
    setWebsites(e.target.value);
    checkIfAllLinesGreen(actualHtmlContent, e.target.value);
  };

  const checkIfAllLinesGreen = (isGreen) => {
    setAllWebsitesArePresent(isGreen);
  };

  const highlightWordsInHtml = (htmlContent, wordsToHighlight) => {
    if (!wordsToHighlight || wordsToHighlight.length === 0) {
      return htmlContent;
    }

    let processedContent = he.decode(htmlContent);
    let filteredKeywords = wordsToHighlight.filter((keyword) => {
      return keyword.length > 0 && keyword !== '';
    });

    return replaceTextInBold(processedContent, filteredKeywords);
  };

  const applyHighlightingAndWait = () => {
    return new Promise((resolve) => {
      if (!editorInitialized || !editorRef.current) {
        resolve(actualHtmlContent);
        return;
      }

      const editor = editorRef.current;
      const currentRawHtml = editor.getContent();
      const newHighlightedHtml = highlightWordsInHtml(
        currentRawHtml,
        wordsInBold
      );

      if (newHighlightedHtml !== currentRawHtml) {
        editor.setContent(newHighlightedHtml);
        setActualHtmlContent(() => {
          resolve(newHighlightedHtml);
          return newHighlightedHtml;
        });
        setActualTextContent(editor.getContent({ format: 'text' }));
      } else {
        resolve(actualHtmlContent);
      }
    });
  };

  useEffect(() => {
    document.title = t('Typix - Détails du texte AdminClient');
    window.dispatchEvent(new Event('locationchange'));
  }, [t]);



  useEffect(() => {
    enableSave();
  }, [redactionData, url, initialTextContent, enableSave]);

  useEffect(() => {
    checkValidForm();
  }, [checkValidForm]);

  useEffect(() => {
    enableSendRevision();
  }, [enableSendRevision, redactionData]);

  useEffect(() => {
    enableDraft();
  }, [enableDraft]);

  useEffect(() => {
    return () => {
      if (order?.id) {
        dispatch(fetchAdminClientTextOrderDisplay(order.id));
      }
    };
  }, [dispatch, order?.id]);

  // Nettoyer le localStorage lors du démontage du composant
  useEffect(() => {
    return () => {
      // Nettoyer l'ID du texte
      localStorage.removeItem('AdminClient_TextId');
      try {
        window.localStorage.removeItem('WriterClient_PendingTextType');
      } catch { }
    };
  }, []);

  if (isFetchLoading || isLoading || isError) return <Loader />;

  if (!redactionData) {
    return (
      <div className="content-center">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h3>❌ Aucune rédaction trouvée</h3>
          <p>
            currentRedaction est null/undefined et aucune donnée dans
            localStorage
          </p>
          <p>Redirection vers la page des projets...</p>
          <button
            onClick={() => history.push('/AdminClient/DetailsProject')}
            className="btn btn-primary"
          >
            Retour aux projets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-center">
      <Form
        ref={formRef}
        onSubmit={validateData}
        style={{
          transition: 'margin-right 0.3s ease',
          marginRight: sidebarCollapsed || !shouldDisplaySidebar ? 0 : sidebarWidth,
        }}
      >
        <FormHeader
          textTitle={
            redactionData?.title || textTitle || t('Nouveau texte AdminClient')
          }
          onBackClick={() => history.goBack()}
          onDownloadClick={() =>
            redactionData ? DownloadOneTextWordType(redactionData.id, user) : {}
          }
          userRole={ROLES.ADMINCLIENT}
        />

        <TextDetailsSection
          t={t}
          textId={redactionData?.id}
          formData={{
            textTitle,
            keyword,
            url,
            titleTag,
            metaDescriptionTag,
          }}
          handlers={{
            setTextTitle,
            setKeyword,
            setUrl,
            setTitleTag,
            setMetaDescriptionTag,
          }}
        />


        {(redactionData?.globalRevisionSent || redactionData?.revisionSent) &&
          redactionData?.revisionText && (
            <RevisionDetails
              t={t}
              textId={redactionData.id}
              revisionRequestAt={redactionData?.revisionRequestAt}
              revisionText={redactionData?.revisionText}
              revisionFileName={redactionData?.revisionFileName}
            />
          )}


        {(() => {
          const shouldShow =
            hasInitialSection ||
            (order?.type === 'PREMIUM' && url && url.trim() !== '');
          console.log('🔍 Debug InitialTextSection display:', {
            hasInitialSection,
            orderType: order?.type,
            url,
            urlTrimmed: url?.trim(),
            shouldShow,
          });
          return shouldShow;
        })() && (
            <div className="row mt-2">
              <InitialTextSection
                isTextFieldDisabled={isTextFieldDisabled}
                formData={redactionData}
                textType={uiTextType}
                url={url}
                initialHtmlContent={initialHtmlContent}
                handleStepOneEditorInit={handleStepOneEditorInit}
                setInitialHtmlContent={setInitialHtmlContent}
                setInitialTextContent={setInitialTextContent}
                setInitialTextWordCount={setInitialTextWordCount}
                userRole={user.role}
                t={t}
                isNotTranslation={isEditorial}
                isSaveDisabled={isSaveDisabled}
              />
            </div>
          )}

        <div className="row mt-2">
          <div className="col-12">
            <div>
              <div className="label-title label-text-h">
                {t('Ajouter ou compléter ci-dessous votre contenu')} :
              </div>

              <div className="box-wrapper-editor">
                <CustomEditor
                  disabled={false}
                  textId={redactionData?.id}
                  content={
                    (actualHtmlContent && actualHtmlContent.length > 0)
                      ? actualHtmlContent
                      : actualTextContent
                  }
                  onInit={(evt, editor) => handleEditorInit(editor)}
                  onChange={(value, editor) => {
                    let textValue = editor.getContent({ format: 'text' });
                    const newWordCount = editor.plugins.wordcount.getCount();
                    debouncedSetActualTextWordCount(newWordCount);


                    scheduleSave(
                      textValue,
                      value,
                      currentRedaction,
                      initialHtmlContent,
                      initialTextContent,
                      'SMART'
                    );


                    setActualHtmlContent(value);
                    setActualTextContent(value);
                    setAnalyseText(textValue);

                    checkIfAllLinesGreen(value, websites);
                  }}
                  t={t}
                  orderType="SMART"
                />
              </div>
            </div>

            <SavingIndicator isSaving={isSaving} />

            <div className="d-flex justify-content-between flex-wrap mt-3">


              <div>

                <div className="d-flex">


                  {!redactionData?.id || (!redactionData?.actualTextContent && !redactionData?.actualHtmlContent) ? (

                    <button
                      type="button"
                      className="btn"
                      onClick={async () => {
                        const finalHtml = await applyHighlightingAndWait();
                        await handleSave('manualDraft');
                      }}
                      disabled={!actualTextContent || actualTextContent.trim().length === 0}
                      style={{
                        background: (!actualTextContent || actualTextContent.trim().length === 0) ? '#f5f5f5' : '#6458F1',
                        border: 'none',
                        color: (!actualTextContent || actualTextContent.trim().length === 0) ? '#999' : 'white',
                        borderRadius: '6px',
                        padding: '12px 18px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        boxShadow: '0 0 12px 1px rgba(0,0,0,0.05)',
                        textTransform: 'none',
                        cursor: (!actualTextContent || actualTextContent.trim().length === 0) ? 'not-allowed' : 'pointer',
                        opacity: (!actualTextContent || actualTextContent.trim().length === 0) ? 0.6 : 1,
                      }}
                    >
                      {t('Brouillon')}
                    </button>
                  ) : (
                    // Si le texte est déjà en brouillon ou livré, afficher le dropdown complet
                    <div style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => setActionsOpen(!actionsOpen)}
                        style={{
                          background: '#6458F1',
                          border: 'none',
                          color: '#ffffff',
                          borderRadius: '6px',
                          padding: '10px 18px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          boxShadow: '0 0 12px 1px rgba(0,0,0,0.05)',
                          textTransform: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        {t('Actions')} | <i className={`fa fa-chevron-${actionsOpen ? 'down' : 'up'}`} />
                      </button>
                      {actionsOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            bottom: '100%',
                            marginBottom: 8,
                            background: '#fff',
                            borderRadius: 12,
                            boxShadow:
                              '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
                            border: '1px solid #e5e7eb'
                          }}
                        >
                          {!redactionData?.delivered ? (
                            // Actions pour texte en brouillon : Brouillon, Livrer
                            <>
                              <div
                                style={{
                                  padding: '12px 16px',
                                  borderBottom: '1px solid #f0f0f0',
                                  borderTopLeftRadius: 12,
                                  borderTopRightRadius: 12,
                                  background: '#F5F3FF',
                                  color: '#6458F1',
                                  cursor: 'pointer'
                                }}
                                onClick={async () => {
                                  setActionsOpen(false);
                                  const finalHtml = await applyHighlightingAndWait();
                                  await handleSave('manualDraft');
                                }}
                              >
                                {t('Brouillon')}
                              </div>
                              <div
                                style={{ padding: '12px 16px', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, cursor: 'pointer', background: '#F5F3FF', color: '#6458F1' }}
                                onClick={async () => {
                                  setActionsOpen(false);
                                  const finalHtml = await applyHighlightingAndWait();
                                  await deliverText('manualDeliver', finalHtml);
                                }}
                              >
                                {t('Livrer')}
                              </div>
                            </>
                          ) : (
                            // Actions pour texte livré : Valider, Publier
                            <>
                              <div
                                style={{
                                  padding: '12px 16px',
                                  borderBottom: '1px solid #f0f0f0',
                                  borderTopLeftRadius: 12,
                                  borderTopRightRadius: 12,
                                  background: '#F5F3FF',
                                  color: '#6458F1',
                                  cursor: 'pointer'
                                }}
                                onClick={async () => {
                                  setActionsOpen(false);
                                  const finalHtml = await applyHighlightingAndWait();
                                  try {
                                    const payload = {
                                      actualTextContent: actualTextContent || '',
                                      actualHtmlContent: finalHtml || actualHtmlContent || ''
                                    };
                                    await axiosPrivate.put(`/texts/external/${redactionData?.id}/validate`, payload);
                                    setErrorSuccessMsg(t('Texte validé avec succès'));
                                    setIsSuccess(true);
                                    setErrorSuccess(true);

                                  } catch (error) {
                                    console.error('Erreur lors de la validation:', error);
                                    setErrorSuccessMsg(t('Erreur lors de la validation'));
                                    setIsSuccess(false);
                                    setErrorSuccess(true);
                                  }
                                }}
                              >
                                {t('Valider')}
                              </div>
                              <div
                                style={{ padding: '12px 16px', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, cursor: 'pointer', background: '#F5F3FF', color: '#6458F1' }}
                                onClick={async () => {
                                  setActionsOpen(false);
                                  try {
                                    const payload = {
                                      textId: redactionData?.id || '',
                                      deployed: true,
                                      url: url || ''
                                    };
                                    await axiosPrivate.post('/texts/external/deploy-new-url', payload);
                                    setErrorSuccessMsg(t('Texte publié avec succès'));
                                    setIsSuccess(true);
                                    setErrorSuccess(true);
                                  } catch (error) {
                                    console.error('Erreur lors de la publication:', error);
                                    setErrorSuccessMsg(t('Erreur lors de la publication'));
                                    setIsSuccess(false);
                                    setErrorSuccess(true);
                                  }
                                }}
                              >
                                {t('Publier')}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Form>

      {shouldDisplaySidebar && (
        <TextDisplaySidebar
          ref={sidebarRef}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          width={sidebarWidth}
          setWidth={setSidebarWidth}
          combinedKeywords={combinedKeywords}
          autoKeywordsGenerationLoading={autoKeywordsGenerationLoading}
          keywords={keywords}
          textType={uiTextType}
          orderContentType={uiTextType}
          websites={websites}
          actualHtmlContent={actualHtmlContent}
          actualTextContent={actualTextContent}
          isDelivered={redactionData?.delivered}
          userRole={user.role}
          orderWebsite={
            (order?.website?.urlSite || redactionData?.orderWebsite || '')
          }
          country={order?.website?.country}
          t={t}
          keywordStuffingLoading={keywordStuffingLoading}
          keyword={keyword}
          list={list}
          language={projectLanguage}
          maxWords={redactionData?.maxWords || order?.maxWords || 1000}
          wordCount={actualTextWordCount}
          initialContentScore={redactionData?.initialScoreData}
          textId={redactionData?.id || null}
          preCalculatedScoreData={redactionData?.initialScoreData || null}
          onScoreDataChange={setCurrentContentScore}
          handleInputChange={handleInputChange}
          handleKeywordChange={handleKeywordChange}
          handleGapUpdate={handleGapUpdate}
          checkIfAllLinesGreen={checkIfAllLinesGreen}
          mode="adminClient"
          onGenerateKeywords={() => {
            keywordsAutoGeneration(
              currentRedaction?.orderId || '',
              keyword,
              '',
              url,
              setKeywords,
              setCombinedKeywords,
              setCombinedKeywordsList,
              setWebsites,
              setErrorSuccessMsg,
              setIsSuccess,
              setErrorSuccess,
              t,
              setAutokeywordGenerationLoading
            );
          }}
        />
      )}

      <ComparisonModal
        open={showComparison}
        initialText={initialTextContent}
        text={he.decode(actualTextContent)}
        handleClose={() => setShowComparison(false)}
      />

      <ConfirmationModal
        open={confirmation}
        message={confirmationMsg}
        handleClose={() => setConfirmation(false)}
        executeAction={handleSave}
      />

      <KeywordStuffingModal
        open={showKeywordStuffingModal}
        title={keywordStuffingModalTitle}
        message={keywordStuffingModalMsg}
        stuffedKeywords={keywordStuffingModalData}
        showButtonClose={keywordStuffingModalCloseBtn}
        handleClose={() => setShowKeywordStuffingModal(false)}
        executeAction={() => putToDraft('keywordStuffingDraft')}
      />

      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => {
          setErrorSuccess(false);
          if (
            errorSuccessMsg ===
            KeywordStuffingModalPrompts.demandeRevisionKeywordStuffing
          ) {
            history.goBack();
          }
        }}
        success={isSuccess}
        isTextDelivered={isTextDelivered}
        resetFields={resetFields}
        redactionId={redactionData?.id}
        showCreditButton={false}
        executeAction={() => { }}
        toggle={() => setErrorSuccess(!errorSuccess)}
        user={user}
        textType={uiTextType}
        imageUrl={errorSuccessImg}
      />
    </div>
  );
}

function PMTextDisplay() {
  usePageTracking(PROJECT_MANAGER_TEXT_DETAILS_PAGE);

  return (
    <WordsProvider>
      <SemanticScoreProvider>
        <PMTextDisplayContent />
      </SemanticScoreProvider>
    </WordsProvider>
  );
}

export default PMTextDisplay;
